import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * GuestConnectPage — public landing page for /guest/:token URLs.
 * Resolves the token, prompts for password if needed, then forwards to
 * the stream view with a guest session token.
 */
export default function GuestConnectPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState(false);

  useEffect(() => {
    fetch(`/api/guest/info?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(setInfo)
      .catch((err) => setError(err.message));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setWrong(false);
    try {
      const r = await fetch("/api/guest/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await r.json();
      if (!r.ok) {
        if (r.status === 401) {
          setWrong(true);
          setBusy(false);
          return;
        }
        throw new Error(data.error || `HTTP ${r.status}`);
      }
      // Forward to the standard stream page with the issued session token
      navigate(`/connect/${encodeURIComponent(data.session_token)}/${data.device_uuid}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <motion.div
          style={cardStyle}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        >
          <div style={{ fontSize: 16, color: "var(--err)", marginBottom: 8 }}>
            Link unavailable
          </div>
          <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
            {error === "invalid or expired guest link"
              ? "This guest link is invalid, expired, or has reached its usage limit."
              : error}
          </div>
        </motion.div>
      </div>
    );
  }

  if (!info) {
    return (
      <div style={pageStyle}>
        <div style={{ color: "var(--text-dim)", fontSize: 12 }}>Loading…</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <motion.div
        style={cardStyle}
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      >
        <div className="brand-mark" style={{ fontSize: 16, marginBottom: 18 }}>
          vorks<span className="dot" /><span className="con">connect</span>
        </div>

        <div style={{ fontSize: 11, color: "var(--text-mute)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
          Guest access
        </div>
        <div style={{ fontSize: 17, marginBottom: 4 }}>{info.device_name}</div>
        {info.label && (
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 14 }}>
            {info.label}
          </div>
        )}

        <div style={{
          fontSize: 11,
          padding: "8px 10px",
          borderRadius: 5,
          marginBottom: 18,
          background: info.online ? "rgba(74,222,128,0.08)" : "rgba(255,255,255,0.04)",
          border: info.online ? "1px solid rgba(74,222,128,0.25)" : "1px solid var(--line)",
          color: info.online ? "#4ade80" : "var(--text-dim)",
        }}>
          {info.online ? "● Host is online" : "○ Host appears offline"}
        </div>

        <form onSubmit={submit}>
          {info.has_password && (
            <>
              <label style={{ fontSize: 10, color: "var(--text-dim)", letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                Password
              </label>
              <input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  background: "var(--bg-elev)",
                  border: wrong ? "1px solid var(--err)" : "1px solid var(--line)",
                  borderRadius: "var(--r)",
                  color: "var(--text)",
                  fontSize: 13,
                  fontFamily: "var(--mono)",
                  marginBottom: 4,
                }}
              />
              {wrong && (
                <div style={{ fontSize: 11, color: "var(--err)", marginBottom: 12 }}>
                  Wrong password — try again.
                </div>
              )}
            </>
          )}

          <button
            type="submit"
            className="btn primary"
            disabled={busy || !info.online || (info.has_password && !password)}
            style={{ width: "100%", marginTop: 14, padding: "12px" }}
          >
            {busy ? "Connecting…" : "Connect"}
          </button>
        </form>

        <div style={{ marginTop: 18, fontSize: 10, color: "var(--text-faint)", lineHeight: 1.5, textAlign: "center" }}>
          Connection will be TLS-encrypted.<br/>
          Session attributed to the link creator.
        </div>
      </motion.div>
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  background: "var(--bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};
const cardStyle = {
  background: "var(--bg-elev)",
  border: "1px solid var(--line)",
  borderRadius: 10,
  padding: 28,
  width: "100%",
  maxWidth: 380,
  boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
};
